import { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import supabase from '../supabaseClient';
import { SB_URL, SB_KEY, sbSignedUrl } from '../services/supabase-api';
import { Inp } from './ui/FormControls';

const FACTURAR_A_DEFAULT = "SOLAN DE CABRAS, S.L.";

const mesActualISO = () => new Date().toISOString().slice(0, 7);
const ultimoDiaMes = (periodo) => {
  const [y, m] = periodo.split("-").map(Number);
  return new Date(y, m, 0).toISOString().slice(0, 10);
};
const siguienteMesISO = (periodo) => {
  const [y, m] = periodo.split("-").map(Number);
  return new Date(y, m, 1).toISOString().slice(0, 10);
};
const numeroFactura = (periodo) => {
  const [y, m] = periodo.split("-");
  return `D${m}-${y.slice(2)}`;
};
const formatFecha = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES") : "—";
const formatEuros = (n) => `${(Number(n) || 0).toFixed(2)} €`;

const subirPdfFactura = async (blob, numero) => {
  try {
    const carpeta = numero.trim().replace(/[^a-zA-Z0-9_-]/g, "_");
    const nombre = `internacional/facturas/${carpeta}/factura_${Date.now()}.pdf`;
    const r = await fetch(`${SB_URL}/storage/v1/object/Documentos-registros/${nombre}`, {
      method: "POST",
      headers: { "apikey": SB_KEY, "Authorization": `Bearer ${SB_KEY}`, "Content-Type": "application/pdf" },
      body: blob
    });
    if (!r.ok) return null;
    return `${SB_URL}/storage/v1/object/Documentos-registros/${nombre}`;
  } catch { return null; }
};

const generarPdfFactura = (factura, almacenNombre) => {
  const doc = new jsPDF();
  doc.setFontSize(20);
  doc.setFont(undefined, "bold");
  doc.text("FACTURA", 14, 20);
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text(almacenNombre || "", 14, 28);
  doc.text(`Nº: ${factura.numero}`, 150, 20);
  doc.text(`Fecha: ${formatFecha(factura.fecha)}`, 150, 26);
  doc.setFont(undefined, "bold");
  doc.text("FACTURAR A:", 14, 42);
  doc.text(factura.facturar_a || "", 14, 48);
  doc.setFont(undefined, "normal");

  const lineasConCantidad = (factura.lineas || []).filter(l => Number(l.cantidad) > 0);
  autoTable(doc, {
    startY: 58,
    head: [["Concepto", "Cantidad", "Precio", "Importe"]],
    body: lineasConCantidad.map(l => [l.concepto, String(l.cantidad), formatEuros(l.precio), formatEuros(l.importe)]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [30, 58, 95] },
  });

  const finalY = (doc.lastAutoTable?.finalY || 58) + 12;
  doc.setFontSize(13);
  doc.setFont(undefined, "bold");
  doc.text(`TOTAL: ${formatEuros(factura.total)}`, 150, finalY);

  return doc.output("blob");
};

const calcularLineasProforma = async (periodo, almacen_id) => {
  const [tarifasRes, stockRes, salidasRes] = await Promise.all([
    supabase.from('tarifas_facturacion').select('*').eq('almacen_id', almacen_id).eq('activo', true).order('orden', { ascending: true }),
    supabase.from('stock').select('cantidad_palets').eq('almacen_id', almacen_id),
    supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Salida Contenedor')
      .gte('created_at', `${periodo}-01T00:00:00`).lt('created_at', `${siguienteMesISO(periodo)}T00:00:00`),
  ]);
  const tarifas = tarifasRes.data || [];
  const totalPaletsStock = (stockRes.data || []).reduce((s, r) => s + (r.cantidad_palets || 0), 0);
  const salidas = salidasRes.data || [];

  const njsPorTipo = { 1: [], 2: [], 3: [] };
  for (const s of salidas) {
    const t = Number(s.datos?.tipoDestino);
    if ([1, 2, 3].includes(t) && s.datos?.nj) njsPorTipo[t].push(s.datos.nj);
  }
  const todosNjs = [...new Set([...njsPorTipo[1], ...njsPorTipo[2], ...njsPorTipo[3]])];
  const paletsPorNj = {};
  if (todosNjs.length > 0) {
    const { data: pedidosMatch } = await supabase.from('pedidos').select('id,nj').eq('almacen_id', almacen_id).in('nj', todosNjs);
    const pedidoIdPorNj = Object.fromEntries((pedidosMatch || []).map(p => [p.nj, p.id]));
    const pedidoIds = Object.values(pedidoIdPorNj);
    if (pedidoIds.length > 0) {
      const { data: lineasPedidos } = await supabase.from('pedidos_lineas').select('pedido_id,cantidad_palets').in('pedido_id', pedidoIds);
      const paletsPorPedido = {};
      for (const l of (lineasPedidos || [])) paletsPorPedido[l.pedido_id] = (paletsPorPedido[l.pedido_id] || 0) + (l.cantidad_palets || 0);
      for (const nj of todosNjs) {
        const pid = pedidoIdPorNj[nj];
        paletsPorNj[nj] = pid ? (paletsPorPedido[pid] || 0) : 0;
      }
    }
  }
  const sumaPaletsTipo = (tipo) => njsPorTipo[tipo].reduce((s, nj) => s + (paletsPorNj[nj] || 0), 0);
  const contarSalidas = (campo) => salidas.filter(s => s.datos?.[campo] === true).length;

  return tarifas.map(t => {
    let cantidad = 0;
    switch (t.orden) {
      case 1: cantidad = 1; break;
      case 2: cantidad = Math.max(0, totalPaletsStock - 500); break;
      case 3: cantidad = sumaPaletsTipo(1); break;
      case 4: cantidad = sumaPaletsTipo(2); break;
      case 5: cantidad = sumaPaletsTipo(3); break;
      case 6: case 7: case 8: cantidad = 0; break;
      case 9: cantidad = contarSalidas("bolsas") * 20; break;
      case 10: cantidad = contarSalidas("manta") * 1; break;
      case 11: cantidad = contarSalidas("manipulado"); break;
      case 12: cantidad = 0; break;
      default: cantidad = 0;
    }
    const precio = Number(t.precio_unitario) || 0;
    return { concepto: t.concepto, cantidad, precio, importe: Number((cantidad * precio).toFixed(2)) };
  });
};

const ESTADO_STYLE = { borrador: "bg-amber-100 text-amber-700", emitida: "bg-green-100 text-green-700" };
const ESTADO_LABEL = { borrador: "Borrador", emitida: "Emitida" };

// ── OPERARIO (INTERNACIONAL): FACTURACIÓN ─────────────────────────
export const FacturacionSection = ({ currentUser, guardar }) => {
  const almacen_id = currentUser?.almacen_id;

  const [vista, setVista] = useState("lista");

  const [facturas, setFacturas] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [filtroAnio, setFiltroAnio] = useState("");
  const [queryNumero, setQueryNumero] = useState("");

  const [mostrarSelectorMes, setMostrarSelectorMes] = useState(false);
  const [mesSeleccionado, setMesSeleccionado] = useState(mesActualISO());
  const [generando, setGenerando] = useState(false);

  const [facturaActiva, setFacturaActiva] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [emitiendo, setEmitiendo] = useState(false);
  const [confirmarReabrir, setConfirmarReabrir] = useState(false);
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);

  const [tarifasEditables, setTarifasEditables] = useState([]);
  const [cargandoTarifas, setCargandoTarifas] = useState(true);
  const [guardandoTarifas, setGuardandoTarifas] = useState(false);

  const cargarFacturas = () => {
    if (!almacen_id) return;
    setCargandoLista(true);
    supabase.from('facturas').select('*').eq('almacen_id', almacen_id).order('fecha', { ascending: false })
      .then(({ data }) => setFacturas(data || []))
      .finally(() => setCargandoLista(false));
  };
  useEffect(cargarFacturas, [almacen_id]);

  useEffect(() => {
    if (vista !== "tarifas" || !almacen_id) return;
    setCargandoTarifas(true);
    supabase.from('tarifas_facturacion').select('*').eq('almacen_id', almacen_id).order('orden', { ascending: true })
      .then(({ data }) => setTarifasEditables(data || []))
      .finally(() => setCargandoTarifas(false));
  }, [vista, almacen_id]);

  const anios = [...new Set(facturas.map(f => f.periodo?.slice(0, 4)).filter(Boolean))].sort().reverse();
  const facturasFiltradas = facturas.filter(f => {
    if (filtroAnio && f.periodo?.slice(0, 4) !== filtroAnio) return false;
    if (queryNumero.trim() && !(f.numero || "").toLowerCase().includes(queryNumero.trim().toLowerCase())) return false;
    return true;
  });

  const generarProforma = async () => {
    setGenerando(true);
    try {
      const lineas = await calcularLineasProforma(mesSeleccionado, almacen_id);
      const total = Number(lineas.reduce((s, l) => s + l.importe, 0).toFixed(2));
      setFacturaActiva({
        id: null,
        numero: numeroFactura(mesSeleccionado),
        fecha: ultimoDiaMes(mesSeleccionado),
        periodo: mesSeleccionado,
        facturar_a: FACTURAR_A_DEFAULT,
        lineas,
        total,
        estado: "borrador",
      });
      setMostrarSelectorMes(false);
      setVista("editor");
    } finally {
      setGenerando(false);
    }
  };

  const abrirFactura = (f) => {
    setFacturaActiva({ ...f, lineas: f.lineas || [] });
    setVista("editor");
  };

  const actualizarLinea = (idx, campo, valor) => {
    setFacturaActiva(f => {
      const lineas = f.lineas.map((l, i) => {
        if (i !== idx) return l;
        const next = { ...l, [campo]: valor };
        if (campo === "cantidad" || campo === "precio") {
          next.importe = Number(((Number(next.cantidad) || 0) * (Number(next.precio) || 0)).toFixed(2));
        }
        return next;
      });
      const total = Number(lineas.reduce((s, l) => s + (Number(l.importe) || 0), 0).toFixed(2));
      return { ...f, lineas, total };
    });
  };
  const añadirLineaLibre = () => setFacturaActiva(f => ({ ...f, lineas: [...f.lineas, { concepto: "", cantidad: 0, precio: 0, importe: 0 }] }));
  const quitarLinea = (idx) => setFacturaActiva(f => {
    const lineas = f.lineas.filter((_, i) => i !== idx);
    const total = Number(lineas.reduce((s, l) => s + (Number(l.importe) || 0), 0).toFixed(2));
    return { ...f, lineas, total };
  });

  const guardarFactura = async (estadoFinal) => {
    const payload = {
      almacen_id,
      numero: facturaActiva.numero.trim(),
      fecha: facturaActiva.fecha,
      periodo: facturaActiva.periodo,
      facturar_a: facturaActiva.facturar_a.trim(),
      lineas: facturaActiva.lineas,
      total: facturaActiva.total,
      estado: estadoFinal,
      usuario: currentUser?.user,
    };
    if (facturaActiva.id) {
      const { data } = await supabase.from('facturas').update(payload).eq('id', facturaActiva.id).select().single();
      return data;
    }
    const { data } = await supabase.from('facturas').insert(payload).select().single();
    return data;
  };

  const guardarBorrador = async () => {
    setGuardando(true);
    try {
      const saved = await guardarFactura("borrador");
      if (saved) {
        setVista("lista");
        setFacturaActiva(null);
        cargarFacturas();
      }
    } finally {
      setGuardando(false);
    }
  };

  const emitirFactura = async () => {
    setEmitiendo(true);
    try {
      const saved = await guardarFactura("borrador");
      if (!saved) return;
      const blob = generarPdfFactura(facturaActiva, currentUser?.almacen_nombre || "");
      const pdfUrl = await subirPdfFactura(blob, saved.numero);
      if (!pdfUrl) return;
      await supabase.from('facturas').update({ estado: "emitida" }).eq('id', saved.id);
      await guardar("Factura", { numero: saved.numero, periodo: saved.periodo, facturar_a: saved.facturar_a, total: saved.total, pdfUrl });
      setVista("lista");
      setFacturaActiva(null);
      cargarFacturas();
    } finally {
      setEmitiendo(false);
    }
  };

  const reabrirFactura = async () => {
    await supabase.from('facturas').update({ estado: "borrador" }).eq('id', facturaActiva.id);
    setFacturaActiva(f => ({ ...f, estado: "borrador" }));
    setConfirmarReabrir(false);
  };

  const descargarPdf = async () => {
    const { data } = await supabase.from('registros').select('*').eq('almacen_id', almacen_id).eq('tipo', 'Factura').eq('datos->>numero', facturaActiva.numero).order('created_at', { ascending: false }).limit(1).maybeSingle();
    const url = data?.datos?.pdfUrl;
    if (!url) return;
    const signed = await sbSignedUrl(url);
    window.open(signed || url, "_blank");
  };

  const eliminarFactura = async () => {
    const { data } = await supabase.from('facturas').delete().eq('id', facturaActiva.id).select();
    setConfirmarEliminar(false);
    if (data && data.length > 0) {
      setVista("lista");
      setFacturaActiva(null);
      cargarFacturas();
    }
  };

  const actualizarTarifa = (idx, campo, valor) => setTarifasEditables(t => t.map((r, i) => i !== idx ? r : { ...r, [campo]: valor }));
  const guardarTarifas = async () => {
    setGuardandoTarifas(true);
    try {
      await Promise.all(tarifasEditables.map(t =>
        supabase.from('tarifas_facturacion').update({ precio_unitario: Number(t.precio_unitario) || 0, activo: !!t.activo }).eq('id', t.id)
      ));
    } finally {
      setGuardandoTarifas(false);
    }
  };

  // ── VISTA: TARIFAS ──────────────────────────────────────────────
  if (vista === "tarifas") {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
          <h2 className="font-bold text-gray-800">💶 Tarifas de facturación</h2>
          <button onClick={() => setVista("lista")} className="text-sm text-gray-500 hover:text-gray-700">← Volver</button>
        </div>
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          {cargandoTarifas ? (
            <p className="text-sm text-gray-400 text-center py-8">Cargando...</p>
          ) : (
            <table className="w-full text-sm min-w-max">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Concepto</th>
                  <th className="px-4 py-2 text-left">Precio unitario</th>
                  <th className="px-4 py-2 text-left">Activo</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tarifasEditables.map((t, i) => (
                  <tr key={t.id}>
                    <td className="px-4 py-2">{t.concepto}</td>
                    <td className="px-4 py-2">
                      <input type="number" step="0.01" value={t.precio_unitario} onChange={e => actualizarTarifa(i, "precio_unitario", e.target.value)} className="w-28 border rounded-lg px-2 py-1 text-sm" />
                    </td>
                    <td className="px-4 py-2">
                      <input type="checkbox" className="w-4 h-4 accent-violet-600" checked={!!t.activo} onChange={e => actualizarTarifa(i, "activo", e.target.checked)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <button onClick={guardarTarifas} disabled={guardandoTarifas} className="w-full bg-violet-700 text-white font-bold py-3 rounded-xl hover:bg-violet-800 disabled:opacity-40 transition-all">
          {guardandoTarifas ? "⏳ Guardando..." : "Guardar cambios"}
        </button>
        <p className="text-xs text-gray-400 text-center">Los cambios solo afectan a proformas generadas a partir de ahora.</p>
      </div>
    );
  }

  // ── VISTA: EDITOR / DETALLE DE FACTURA ─────────────────────────
  if (vista === "editor" && facturaActiva) {
    const esEmitida = facturaActiva.estado === "emitida";
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
          <h2 className="font-bold text-gray-800">🧾 {facturaActiva.id ? `Factura ${facturaActiva.numero}` : "Nueva proforma"}</h2>
          <button onClick={() => { setVista("lista"); setFacturaActiva(null); }} className="text-sm text-gray-500 hover:text-gray-700">← Volver</button>
        </div>

        <div className="bg-white rounded-xl shadow p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ESTADO_STYLE[facturaActiva.estado]}`}>{ESTADO_LABEL[facturaActiva.estado]}</span>
            <span className="text-xs text-gray-400">Periodo: {facturaActiva.periodo}</span>
          </div>
          {esEmitida ? (
            <div className="text-sm text-gray-700 space-y-1">
              <p><span className="font-semibold">Nº:</span> {facturaActiva.numero}</p>
              <p><span className="font-semibold">Fecha:</span> {formatFecha(facturaActiva.fecha)}</p>
              <p><span className="font-semibold">Facturar a:</span> {facturaActiva.facturar_a}</p>
            </div>
          ) : (
            <>
              <Inp label="Nº de factura" value={facturaActiva.numero} onChange={v => setFacturaActiva(f => ({ ...f, numero: v }))} />
              <Inp label="Fecha" type="date" value={facturaActiva.fecha} onChange={v => setFacturaActiva(f => ({ ...f, fecha: v }))} />
              <Inp label="Facturar a" value={facturaActiva.facturar_a} onChange={v => setFacturaActiva(f => ({ ...f, facturar_a: v }))} />
            </>
          )}
        </div>

        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm min-w-max">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Concepto</th>
                <th className="px-4 py-2 text-left">Cantidad</th>
                <th className="px-4 py-2 text-left">Precio</th>
                <th className="px-4 py-2 text-left">Importe</th>
                {!esEmitida && <th className="px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {facturaActiva.lineas.map((l, i) => (
                <tr key={i}>
                  <td className="px-4 py-2">
                    {esEmitida ? l.concepto : (
                      <input value={l.concepto} onChange={e => actualizarLinea(i, "concepto", e.target.value)} className="w-full border rounded-lg px-2 py-1 text-sm" />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {esEmitida ? l.cantidad : (
                      <input type="number" value={l.cantidad} onChange={e => actualizarLinea(i, "cantidad", e.target.value)} className="w-20 border rounded-lg px-2 py-1 text-sm" />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {esEmitida ? formatEuros(l.precio) : (
                      <input type="number" step="0.01" value={l.precio} onChange={e => actualizarLinea(i, "precio", e.target.value)} className="w-24 border rounded-lg px-2 py-1 text-sm" />
                    )}
                  </td>
                  <td className="px-4 py-2 font-semibold">{formatEuros(l.importe)}</td>
                  {!esEmitida && (
                    <td className="px-4 py-2"><button onClick={() => quitarLinea(i)} className="text-xs text-red-500 underline">Quitar</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!esEmitida && (
          <button onClick={añadirLineaLibre} className="w-full border-2 border-dashed border-gray-300 rounded-xl py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all">
            + Añadir línea libre
          </button>
        )}

        <div className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
          <span className="font-bold text-gray-800">TOTAL</span>
          <span className="text-xl font-bold text-gray-800">{formatEuros(facturaActiva.total)}</span>
        </div>

        {!esEmitida ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button onClick={guardarBorrador} disabled={guardando || emitiendo} className="flex-1 border border-gray-300 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">
                {guardando ? "⏳ Guardando..." : "Guardar borrador"}
              </button>
              <button onClick={emitirFactura} disabled={guardando || emitiendo} className="flex-1 bg-violet-700 text-white font-bold py-3 rounded-xl hover:bg-violet-800 disabled:opacity-40 transition-all">
                {emitiendo ? "⏳ Emitiendo..." : "Emitir factura →"}
              </button>
            </div>
            {facturaActiva.id && (
              <button onClick={() => setConfirmarEliminar(true)} className="w-full text-xs text-red-500 underline py-1">Eliminar factura</button>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={descargarPdf} className="flex-1 bg-blue-50 border border-blue-200 text-blue-700 font-bold py-3 rounded-xl hover:bg-blue-100 transition-all">📥 Descargar PDF</button>
            <button onClick={() => setConfirmarReabrir(true)} className="flex-1 border border-gray-300 text-gray-700 font-bold py-3 rounded-xl hover:bg-gray-50 transition-all">🔓 Reabrir</button>
          </div>
        )}

        {confirmarReabrir && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-4 space-y-4">
              <h2 className="font-bold text-gray-800">⚠️ Reabrir factura</h2>
              <p className="text-sm text-gray-600">Vas a modificar una factura ya emitida.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmarReabrir(false)} className="flex-1 border border-gray-300 text-gray-600 font-bold py-2 rounded-xl hover:bg-gray-50">Cancelar</button>
                <button onClick={reabrirFactura} className="flex-1 bg-amber-600 text-white font-bold py-2 rounded-xl hover:bg-amber-700">Reabrir</button>
              </div>
            </div>
          </div>
        )}

        {confirmarEliminar && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-4 space-y-4">
              <h2 className="font-bold text-gray-800">🗑️ Eliminar factura</h2>
              <p className="text-sm text-gray-600">Esta acción es permanente y no se puede deshacer.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmarEliminar(false)} className="flex-1 border border-gray-300 text-gray-600 font-bold py-2 rounded-xl hover:bg-gray-50">Cancelar</button>
                <button onClick={eliminarFactura} className="flex-1 bg-red-600 text-white font-bold py-2 rounded-xl hover:bg-red-700">Eliminar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── VISTA: LISTA (default) ──────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-gray-800">🧾 Facturación</h2>
          <div className="flex gap-2">
            <button onClick={() => setMostrarSelectorMes(true)} className="bg-violet-700 text-white text-xs font-bold px-3 py-2 rounded-lg hover:bg-violet-800">Generar proforma</button>
            <button onClick={() => setVista("tarifas")} className="bg-gray-100 text-gray-700 text-xs font-bold px-3 py-2 rounded-lg hover:bg-gray-200 border border-gray-200">Tarifas</button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)} className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400">
            <option value="">Todos los años</option>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <input value={queryNumero} onChange={e => setQueryNumero(e.target.value)} placeholder="Buscar por nº de factura..." className="flex-1 min-w-[160px] border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
        </div>
      </div>

      <div className="space-y-2">
        {cargandoLista && <p className="text-sm text-gray-400 text-center py-4">Cargando...</p>}
        {!cargandoLista && facturasFiltradas.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No hay facturas que coincidan.</p>
        )}
        {!cargandoLista && facturasFiltradas.map(f => (
          <div key={f.id} onClick={() => abrirFactura(f)} className="bg-white rounded-xl shadow p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border border-gray-100">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm">{f.numero} <span className="text-gray-400 font-normal">· {f.periodo}</span></p>
              <p className="text-xs text-gray-500">{formatFecha(f.fecha)} · {formatEuros(f.total)}</p>
            </div>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ESTADO_STYLE[f.estado]}`}>{ESTADO_LABEL[f.estado]}</span>
          </div>
        ))}
      </div>

      {mostrarSelectorMes && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-4 space-y-4">
            <h2 className="font-bold text-gray-800 border-b pb-2">Generar proforma</h2>
            <Inp label="Mes" type="month" value={mesSeleccionado} onChange={setMesSeleccionado} />
            <div className="flex gap-2">
              <button onClick={() => setMostrarSelectorMes(false)} disabled={generando} className="flex-1 border border-gray-300 text-gray-600 font-bold py-2 rounded-xl hover:bg-gray-50 disabled:opacity-40">Cancelar</button>
              <button onClick={generarProforma} disabled={generando} className="flex-1 bg-violet-700 text-white font-bold py-2 rounded-xl hover:bg-violet-800 disabled:opacity-40">
                {generando ? "⏳ Generando..." : "Generar →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
